import cashmemodirectService from '../../../services/cashmemodirectService';
import filterService from '../../../services/filterService';

import {
    setCashMemoDirectLoading,
    setCashMemoDirectError,
    setCashMemoDirectList,
    setCashMemoDirectCurrent,
    setCashMemoDirectPagination,
    setCashMemoDirectSort,
    setCashMemoDirectFilters,
    setCashMemoDirectDefaultFilter,
    setCashMemoDirectSettings,
    clearCashMemoDirectCurrent,
    clearCashMemoDirectError,
    setCashMemoDirectFilterLoading,
    changeCashMemoDirectFilterCondition,
    addCashMemoDirectFilterCondition,
    updateCashMemoDirectFilterConditionRule,
    updateCashMemoDirectFilterConditionValue,
    removeCashMemoDirectFilterCondition,
    setCashMemoDirectSelected,
    setCashMemoDirectDownloading,
    resetCashMemoDirectFiltersAndPagination
} from './cashmemodirectSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().cashmemodirect.isSettingsInitialized) {
            dispatch(setCashMemoDirectLoading(true));
            const response = await cashmemodirectService.getSettings();

            // Set the settings
            dispatch(setCashMemoDirectSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setCashMemoDirectDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setCashMemoDirectError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setCashMemoDirectLoading(false));
    }
};

export const fetchCashmemodirects = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().cashmemodirect;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setCashMemoDirectLoading(true));
        const response = await cashmemodirectService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setCashMemoDirectList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchCashmemodirects:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCashMemoDirectError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCashMemoDirectLoading(false));
    }
};

export const fetchCashMemoDirect = (id) => async (dispatch) => {
    try {
        dispatch(setCashMemoDirectLoading(true));
        const response = await cashmemodirectService.getOne(id);
        dispatch(setCashMemoDirectCurrent(response.data));
    } catch (error) {
        dispatch(setCashMemoDirectError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setCashMemoDirectLoading(false));
    }
};

export const createCashMemoDirect = (data) => async (dispatch) => {
    try {
        const response = await cashmemodirectService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateCashMemoDirect = (id, data) => async (dispatch) => {
    try {
        const response = await cashmemodirectService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteCashMemoDirect = (id) => async (dispatch) => {
    try {
        dispatch(setCashMemoDirectLoading(true));
        const response = await cashmemodirectService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setCashMemoDirectError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCashMemoDirectLoading(false));
    }
};

export const downloadCashMemoDirect = (type) => async (dispatch, getState) => {
    try {
        const state = getState().cashmemodirect;

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            export_type: type
        };

        const filter = state.defaultFilter;
        dispatch(setCashMemoDirectDownloading(true));
        const response = await cashmemodirectService.download(finalQueryParams, filter);
        dispatch(setCashMemoDirectDownloading(false));

        return response;
    } catch (error) {
        console.error('Error downloading cashmemodirects:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCashMemoDirectError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCashMemoDirectDownloading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setCashMemoDirectPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setCashMemoDirectSort(sort));
};

export const searchCashmemodirects = (query) => (dispatch, getState) => {
    const currentFilters = getState().cashmemodirect.filters;
    dispatch(setCashMemoDirectFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setCashMemoDirectDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeCashMemoDirectFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addCashMemoDirectFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateCashMemoDirectFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateCashMemoDirectFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeCashMemoDirectFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setCashMemoDirectFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().cashmemodirect.settings;
            dispatch(setCashMemoDirectSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setCashMemoDirectFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setCashMemoDirectFilterLoading(false));
        dispatch(setCashMemoDirectError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setCashMemoDirectSelected(selectedRows));
    };
};

export const clearCurrentCashMemoDirect = () => {
    return (dispatch) => {
        dispatch(clearCashMemoDirectCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetCashMemoDirectFiltersAndPagination());
};